import { PAGINATION_LIMIT } from '@v2/commons/config'
import usePersistentForm from '@v2/commons/hooks/forms/usePersistentValuesForm'
import usePolicyVerification from '@v2/commons/hooks/policyVerification/usePolicyVerification'
import { selectCurrentOrganization } from '@v2/domains/user/selectors/user.selectors'
import { useAppSelector } from '@v2/store/hooks'
import { WbfPolicies } from '@workers-benefit-fund/shared-data'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useGetTasksQuery } from '../api/task.api'
import { type IGetTasksParams, TaskTypeEnum } from '../task.types'

export const useGetTasks = () => {
  const [page, setPage] = useState(0)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const currentOrganization = useAppSelector(selectCurrentOrganization)
  const isProgrammaticFormUpdateRef = useRef<boolean>(true)
  const isAllTaskPermitted = usePolicyVerification(
    WbfPolicies.is_enable_all_tasks,
  )
  const orgId = useMemo(() => {
    setPage(0)
    return (currentOrganization.id as string) || ''
  }, [currentOrganization.id])

  const getTasksParamsForm = usePersistentForm<IGetTasksParams>({
    localStoragePath: `${currentOrganization.id}-tasks`,
    isProgrammaticFormUpdateRef,
    formConfig: {
      mode: 'uncontrolled',
      initialValues: {},
      onValuesChange(values, previousValues) {
        setPage(0)
        if (!!values.action) {
          getTasksParamsForm.setFieldValue('excludeAction', false)
        }
        const arePersistedFilters = !!Object.keys(values).length
        const didUserClearFilters = !!Object.keys(previousValues).length
        if (!didUserClearFilters) {
          setFiltersOpen(arePersistedFilters ? true : false)
        }
      },
    },
  })

  useEffect(() => {
    getTasksParamsForm.setFieldValue(
      'assignedToUserId',
      isAllTaskPermitted
        ? getTasksParamsForm.getValues()['assignedToUserId']
        : 'null',
    )
  }, [isAllTaskPermitted])

  const { data, isLoading, isSuccess, isFetching, refetch } = useGetTasksQuery({
    params: {
      limit: PAGINATION_LIMIT,
      offset: page * PAGINATION_LIMIT,
      type: TaskTypeEnum.FOLLOW_UP,
      ...getTasksParamsForm.getValues(),
      assignedToUserId: isAllTaskPermitted
        ? getTasksParamsForm.getValues()['assignedToUserId']
        : 'null',
    },
    organizationId: orgId,
  })

  useEffect(() => {
    if (page === 0) {
      refetch()
    }
  }, [page])

  const handlePaginate = () => {
    if (isFetching) return

    setPage((prev) => {
      if (
        data &&
        data.totalRecords <= prev * PAGINATION_LIMIT + PAGINATION_LIMIT
      ) {
        return prev
      }
      return ++prev
    })
  }

  const resetPaginate = () => {
    if (page === 0) {
      refetch()
    }
    setPage(0)
  }

  return {
    tasks: data?.records ?? [],
    totalRecords: data?.totalRecords,
    isLoading,
    isSuccess,
    isFetching,
    handlePaginate,
    getTasksParamsForm,
    resetPaginate,
    isProgrammaticFormUpdateRef,
    filtersOpen,
    setFiltersOpen,
  }
}

export default useGetTasks
